import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import TimesheetSettings from './TimesheetSettings';
import {
  LogIn, LogOut, AlertTriangle, Clock, CheckCircle, XCircle, FileSpreadsheet,
  ChevronDown, ChevronRight, Search, X, Filter, Users, Briefcase, Calendar,
  Edit2, Trash2, History, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight as ChevronRightIcon
} from 'lucide-react';
import { UserContext } from '../contexts/UserContext';

const TaskBasedTimesheetManagement = () => {
  const { user } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState('timesheets');
  const [timesheets, setTimesheets] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    total_hours: '0h',
    approved_hours: '0h',
    pending: 0,
    approved: 0,
    rejected: 0,
    admin_types: [],
    categories: [],
    work_types: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    users: [],
    projects: [],
    turbines: [],
    task_categories: [],
    tasks: [],
    date_from: '',
    date_to: ''
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableTurbines, setAvailableTurbines] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [turbineSearch, setTurbineSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showTurbineDropdown, setShowTurbineDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    break_duration: 0,
    type: '',
    projekt: '',
    admin_type: '',
    work_subtype: '',
    note: ''
  });
  const [adminTypes, setAdminTypes] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyTimesheet, setHistoryTimesheet] = useState(null);
  const [showSingleRejectModal, setShowSingleRejectModal] = useState(false);
  const [rejectingTimesheetId, setRejectingTimesheetId] = useState(null);
  const [singleRejectionReason, setSingleRejectionReason] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [taskCategories, setTaskCategories] = useState([]);
  const [taskSubcategories, setTaskSubcategories] = useState([]);
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [discrepancyData, setDiscrepancyData] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteTimesheetId, setDeleteTimesheetId] = useState(null);
  const [expandedProjectCard, setExpandedProjectCard] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting state
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    fetchTimesheets();
    fetchStats();
  }, [filters, currentPage]);

  useEffect(() => {
    fetchUsersAndProjects();
    fetchTaskData();
    fetchAdminTypes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown-container')) {
        setShowTaskDropdown(false);
        setShowTurbineDropdown(false);
        setShowUserDropdown(false);
        setShowProjectDropdown(false);
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const fetchUsersAndProjects = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [usersResponse, projectsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/users/`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/projects/`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAvailableUsers(usersResponse.data || []);
      setAvailableProjects(projectsResponse.data || []);
    } catch (error) {
      console.error('Error fetching users/projects:', error);
    }
  };

  const fetchTaskData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [categoriesResponse, turbinesResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/task-categories/`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/project-turbines/`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const categories = categoriesResponse.data || [];
      setTaskCategories(categories);
      // Extract all subcategories from categories
      const allSubcategories = categories.flatMap(cat =>
        (cat.subcategories || []).map(sub => ({ ...sub, category_name: cat.name, category_id: cat.id }))
      );
      setTaskSubcategories(allSubcategories);
      setAvailableTurbines(turbinesResponse.data || []);
    } catch (error) {
      console.error('Error fetching task data:', error);
    }
  };

  const fetchAdminTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin-types/`, { headers: { Authorization: `Bearer ${token}` } });
      setAdminTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching admin types:', error);
    }
  };

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => { if (v) params.append(key, v); });
        } else if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/?${params}`, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.results) {
        setTimesheets(response.data.results || []);
        setTotalPages(response.data.pagination?.num_pages || 1);
        setTotalCount(response.data.pagination?.count || 0);
      } else {
        setTimesheets(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching timesheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => { if (v) params.append(key, v); });
        } else if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/stats/?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(timesheets.map(ts => ts.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleApprove = async () => {
    if (selectedIds.length === 0) {
      showToastMessage('Please select timesheets to approve');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/approve/`, { timesheet_ids: selectedIds }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage(`${selectedIds.length} timesheet(s) approved`);
      setSelectedIds([]);
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error approving timesheets');
    }
  };

  const handleReject = () => {
    if (selectedIds.length === 0) {
      showToastMessage('Please select timesheets to reject');
      return;
    }
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/reject/`, { timesheet_ids: selectedIds, rejection_reason: rejectionReason }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage(`${selectedIds.length} timesheet(s) rejected`);
      setSelectedIds([]);
      setShowRejectModal(false);
      setRejectionReason('');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error rejecting timesheets');
    }
  };

  const handleDelete = () => {
    if (selectedIds.length === 0) {
      showToastMessage('Please select timesheets to delete');
      return;
    }
    setDeleteType('bulk');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const timesheetIds = deleteType === 'single' ? [deleteTimesheetId] : selectedIds;
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/delete/`, { timesheet_ids: timesheetIds }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage(`${timesheetIds.length} timesheet(s) deleted`);
      setSelectedIds([]);
      setShowDeleteModal(false);
      setDeleteType(null);
      setDeleteTimesheetId(null);
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error deleting timesheets');
      setShowDeleteModal(false);
    }
  };

  const handleEditClick = (timesheet) => {
    setEditingTimesheet(timesheet);
    setEditFormData({
      date: timesheet.date,
      start_time: timesheet.start_time,
      end_time: timesheet.end_time,
      break_duration: timesheet.break_duration || 0,
      type: timesheet.type,
      projekt: timesheet.projekt?.id || timesheet.project?.id || '',
      admin_type: timesheet.admin_type?.id || '',
      work_subtype: timesheet.work_subtype?.id || '',
      note: timesheet.note || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/${editingTimesheet.id}/edit/`, editFormData, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage('Timesheet updated successfully');
      setShowEditModal(false);
      setEditingTimesheet(null);
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error updating timesheet');
    }
  };

  const handleHistoryClick = (timesheet) => {
    setHistoryTimesheet(timesheet);
    setShowHistoryModal(true);
  };

  const handleSingleDelete = (timesheetId) => {
    setDeleteTimesheetId(timesheetId);
    setDeleteType('single');
    setShowDeleteModal(true);
  };

  const handleSingleRejectClick = (timesheetId) => {
    setRejectingTimesheetId(timesheetId);
    setShowSingleRejectModal(true);
  };

  const confirmSingleReject = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/reject/`, { timesheet_ids: [rejectingTimesheetId], rejection_reason: singleRejectionReason }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage('Timesheet rejected');
      setShowSingleRejectModal(false);
      setRejectingTimesheetId(null);
      setSingleRejectionReason('');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error rejecting timesheet');
    }
  };

  const handleSingleApprove = async (timesheetId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/approve/`, { timesheet_ids: [timesheetId] }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage('Timesheet approved');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error approving timesheet');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-600',
      approved: 'bg-emerald-50 text-emerald-600',
      rejected: 'bg-red-50 text-red-600',
      'partially-approved': 'bg-blue-50 text-blue-600',
      mixed: 'bg-violet-50 text-violet-600'
    };
    const dots = {
      pending: 'bg-amber-500',
      approved: 'bg-emerald-500',
      rejected: 'bg-red-500',
      'partially-approved': 'bg-blue-500',
      mixed: 'bg-violet-500'
    };
    const labels = {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      'partially-approved': 'Partial',
      mixed: 'Mixed'
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md ${styles[status] || styles.pending}`}>
        <span className={`w-1 h-1 rounded-full ${dots[status] || dots.pending}`} />
        {labels[status] || 'Pending'}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTimeDifference = (minutes) => {
    if (minutes === 0) return '0 min';
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    const sign = minutes > 0 ? '+' : '-';
    if (hours > 0 && mins > 0) return `${sign}${hours}h ${mins}m`;
    if (hours > 0) return `${sign}${hours}h`;
    return `${sign}${mins}m`;
  };

  const handleDiscrepancyClick = (timesheet) => {
    if (timesheet.time_discrepancy) {
      setDiscrepancyData({
        ...timesheet.time_discrepancy,
        user: timesheet.user,
        date: timesheet.date,
        project: timesheet.projekt?.name || timesheet.project?.name
      });
      setShowDiscrepancyModal(true);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-amber-500';
      case 'medium': return 'text-brand-500';
      case 'high': return 'text-red-500';
      default: return 'text-steel-400';
    }
  };

  const parseHours = (hoursStr) => {
    if (!hoursStr) return 0;
    if (typeof hoursStr === 'number') return hoursStr;
    const str = String(hoursStr);
    const match = str.match(/(\d+)h\s*(\d+)?min?/);
    if (match) {
      const h = parseInt(match[1]) || 0;
      const m = parseInt(match[2]) || 0;
      return h + m / 60;
    }
    return parseFloat(str) || 0;
  };

  const formatHoursDecimal = (hoursStr) => {
    const hours = parseHours(hoursStr);
    return `${hours.toFixed(2).replace('.', ',')}h`;
  };

  const getFullType = (timesheet) => {
    // Task-based timesheet - use task_category_name and task_subcategory_name if available
    if (timesheet.task_category_name || timesheet.task_subcategory_name) {
      if (timesheet.task_subcategory_name && timesheet.task_category_name) {
        return `${timesheet.task_category_name} / ${timesheet.task_subcategory_name}`;
      }
      return timesheet.task_subcategory_name || timesheet.task_category_name;
    }
    // Fallback for legacy timesheets without task data
    const typeName = timesheet.type || '-';
    if (timesheet.admin_type && timesheet.admin_type.name) {
      return `${typeName} - ${timesheet.admin_type.name}`;
    }
    return typeName;
  };

  const groupTimesheets = (timesheets) => {
    const grouped = [];
    const groups = {};

    timesheets.forEach(ts => {
      const groupKey = `${ts.user.id}-${ts.date}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          user: ts.user,
          date: ts.date,
          items: [],
          totalHours: 0,
          isGroup: true
        };
      }
      groups[groupKey].items.push(ts);
      const [hours, minutes] = ts.worked_hours.split('h ');
      const totalMinutes = parseInt(hours) * 60 + parseInt(minutes || 0);
      groups[groupKey].totalHours += totalMinutes;
    });

    Object.values(groups).forEach(group => {
      if (group.items.length === 1) {
        grouped.push({ ...group.items[0], isGroup: false });
      } else {
        const hours = Math.floor(group.totalHours / 60);
        const minutes = group.totalHours % 60;
        group.displayHours = `${hours}h ${minutes}min`;

        const statuses = group.items.map(item => item.status);
        const approvedCount = statuses.filter(s => s === 'approved').length;
        const rejectedCount = statuses.filter(s => s === 'rejected').length;
        const pendingCount = statuses.filter(s => s === 'pending').length;

        if (approvedCount === group.items.length) group.groupStatus = 'approved';
        else if (rejectedCount === group.items.length) group.groupStatus = 'rejected';
        else if (pendingCount === group.items.length) group.groupStatus = 'pending';
        else if (approvedCount > 0) group.groupStatus = 'partially-approved';
        else group.groupStatus = 'mixed';

        grouped.push(group);
      }
    });

    return grouped.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
    });
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handleResetFilters = () => {
    setFilters({ status: 'all', users: [], projects: [], turbines: [], task_categories: [], tasks: [], date_from: '', date_to: '' });
    setUserSearch('');
    setProjectSearch('');
    setTurbineSearch('');
    setCategorySearch('');
    setTaskSearch('');
  };

  const handleUserToggle = (userId) => {
    const newUsers = filters.users.includes(userId) ? filters.users.filter(id => id !== userId) : [...filters.users, userId];
    setFilters({ ...filters, users: newUsers });
  };

  const filteredUsers = availableUsers.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    return fullName.includes(userSearch.toLowerCase());
  });

  const handleProjectToggle = (projectId) => {
    const newProjects = filters.projects.includes(projectId) ? filters.projects.filter(id => id !== projectId) : [...filters.projects, projectId];
    setFilters({ ...filters, projects: newProjects });
  };

  const filteredProjects = availableProjects.filter(project => project.name.toLowerCase().includes(projectSearch.toLowerCase()));

  const handleTurbineToggle = (turbineId) => {
    const newTurbines = filters.turbines.includes(turbineId) ? filters.turbines.filter(id => id !== turbineId) : [...filters.turbines, turbineId];
    setFilters({ ...filters, turbines: newTurbines });
  };

  const filteredTurbines = availableTurbines.filter(turbine => {
    const turbineName = `T${turbine.turbine_number}${turbine.turbine_id ? ` (${turbine.turbine_id})` : ''}`;
    return turbineName.toLowerCase().includes(turbineSearch.toLowerCase());
  });

  const availableTasks = taskSubcategories.map(sub => ({
    value: sub.id.toString(),
    label: sub.name,
    category: sub.category_name,
    category_id: sub.category_id
  }));

  const handleTaskToggle = (taskValue) => {
    const newTasks = filters.tasks.includes(taskValue) ? filters.tasks.filter(t => t !== taskValue) : [...filters.tasks, taskValue];
    setFilters({ ...filters, tasks: newTasks });
  };

  const filteredTasks = availableTasks.filter(task => task.label.toLowerCase().includes(taskSearch.toLowerCase()));

  const handleCategoryToggle = (categoryId) => {
    const newCategories = filters.task_categories.includes(categoryId) ? filters.task_categories.filter(c => c !== categoryId) : [...filters.task_categories, categoryId];
    setFilters({ ...filters, task_categories: newCategories });
  };

  const filteredCategories = taskCategories.filter(category => category.name.toLowerCase().includes(categorySearch.toLowerCase()));

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();

      if (selectedIds.length > 0) {
        selectedIds.forEach(id => params.append('timesheet_ids', id));
      } else {
        Object.entries(filters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(v => { if (v) params.append(key, v); });
          } else if (value && value !== 'all') {
            params.append(key, value);
          }
        });
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/export-excel/?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'timesheet_report.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToastMessage('Export downloaded successfully');
    } catch (error) {
      showToastMessage('Error exporting to Excel');
    }
  };

  const hasFilters = filters.status !== 'all' || filters.users.length > 0 || filters.projects.length > 0 || filters.turbines.length > 0 || filters.task_categories.length > 0 || filters.tasks.length > 0 || filters.date_from || filters.date_to;

  // Calculate task category summary (task-based version)
  const taskCategorySummary = {};
  const taskSummary = {};
  timesheets.forEach(ts => {
    // By task category
    const categoryName = ts.task_category_name || ts.type || 'Other';
    const categoryId = ts.task_category || categoryName;
    if (!taskCategorySummary[categoryId]) {
      taskCategorySummary[categoryId] = {
        id: categoryId,
        name: categoryName,
        hours: 0,
        count: 0,
        color: '#6366f1'
      };
    }
    taskCategorySummary[categoryId].hours += parseHours(ts.worked_hours);
    taskCategorySummary[categoryId].count += 1;

    // By task (subcategory)
    if (ts.task_subcategory_name) {
      const taskName = ts.task_subcategory_name;
      if (!taskSummary[taskName]) {
        taskSummary[taskName] = { name: taskName, hours: 0, count: 0 };
      }
      taskSummary[taskName].hours += parseHours(ts.worked_hours);
      taskSummary[taskName].count += 1;
    }
  });
  const categoryCards = Object.values(taskCategorySummary).filter(t => t.hours > 0).sort((a, b) => b.hours - a.hours);
  const taskCards = Object.values(taskSummary).filter(t => t.hours > 0).sort((a, b) => b.hours - a.hours);

  // Calculate project summary
  const projectSummary = {};
  timesheets.forEach(ts => {
    const projectName = ts.projekt?.name || 'No Project';
    const projectId = ts.projekt?.id || 0;
    if (!projectSummary[projectId]) {
      projectSummary[projectId] = { name: projectName, totalHours: 0, types: {} };
    }
    const hours = parseHours(ts.worked_hours);
    projectSummary[projectId].totalHours += hours;
    const typeName = getFullType(ts);
    if (!projectSummary[projectId].types[typeName]) {
      projectSummary[projectId].types[typeName] = 0;
    }
    projectSummary[projectId].types[typeName] += hours;
  });
  const projects = Object.values(projectSummary).filter(p => p.totalHours > 0);

  if (loading && timesheets.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-steel-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-steel-600 text-sm font-medium">Loading timesheets...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-black text-graphite-900">Timesheets Management ( TB )</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('timesheets')}
            className={`h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'timesheets' ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'bg-white border border-steel-200 text-steel-600 hover:bg-steel-50'}`}
          >
            Timesheets
          </button>
          {user?.isCompanyAdmin && (
            <button
              onClick={() => setActiveTab('settings')}
              className={`h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'settings' ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'bg-white border border-steel-200 text-steel-600 hover:bg-steel-50'}`}
            >
              Settings
            </button>
          )}
          {activeTab === 'timesheets' && user?.isCompanyAdmin && (
            <button onClick={handleExportExcel} className="h-9 px-4 bg-graphite-900 text-white rounded-xl hover:bg-graphite-800 flex items-center gap-1.5 font-bold uppercase tracking-wide text-[10px] transition-all">
              <FileSpreadsheet size={14} /> Export Excel
            </button>
          )}
        </div>
      </div>

      {activeTab === 'timesheets' && (
        <>
          {/* Stats Cards - Line 1: Main stats + Activity Types */}
          <div className="flex flex-wrap gap-3 items-center">
            {/* Label */}
            <span className="text-[10px] font-bold text-graphite-700 uppercase tracking-widest">Summary:</span>

            {/* Main Stats */}
            <div className="bg-white rounded-xl px-4 py-3 border border-steel-100 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-brand-500" />
              </div>
              <div>
                <p className="text-xl font-black text-graphite-900 leading-tight">{formatHoursDecimal(stats.total_hours || '0h')}</p>
                <p className="text-[10px] font-bold text-steel-600 uppercase tracking-widest">Total</p>
              </div>
            </div>
            <div className="bg-white rounded-xl px-4 py-3 border border-steel-100 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xl font-black text-emerald-600 leading-tight">{formatHoursDecimal(stats.approved_hours || '0h')}</p>
                <p className="text-[10px] font-bold text-steel-600 uppercase tracking-widest">Approved</p>
              </div>
            </div>
            <div className="bg-white rounded-xl px-4 py-3 border border-steel-100 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xl font-black text-amber-600 leading-tight">{stats.pending || 0}</p>
                <p className="text-[10px] font-bold text-steel-600 uppercase tracking-widest">Pending</p>
              </div>
            </div>
            <div className="bg-white rounded-xl px-4 py-3 border border-steel-100 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-xl font-black text-red-600 leading-tight">{stats.rejected || 0}</p>
                <p className="text-[10px] font-bold text-steel-600 uppercase tracking-widest">Rejected</p>
              </div>
            </div>

            {/* Separator */}
            {categoryCards.length > 0 && <div className="w-px h-12 bg-steel-200 self-center mx-1" />}

            {/* Task Categories */}
            {categoryCards.map((catData, idx) => {
              const colors = [
                { bg: 'bg-blue-100', icon: 'text-blue-500', value: 'text-blue-600' },
                { bg: 'bg-violet-100', icon: 'text-violet-500', value: 'text-violet-600' },
                { bg: 'bg-cyan-100', icon: 'text-cyan-500', value: 'text-cyan-600' },
                { bg: 'bg-pink-100', icon: 'text-pink-500', value: 'text-pink-600' },
                { bg: 'bg-indigo-100', icon: 'text-indigo-500', value: 'text-indigo-600' },
                { bg: 'bg-teal-100', icon: 'text-teal-500', value: 'text-teal-600' },
              ];
              const color = colors[idx % colors.length];
              return (
                <div key={catData.id} className="bg-white rounded-xl px-4 py-3 border border-steel-100 shadow-sm flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${color.bg} flex items-center justify-center`}>
                    <Briefcase className={`h-5 w-5 ${color.icon}`} />
                  </div>
                  <div>
                    <p className={`text-xl font-black ${color.value} leading-tight`}>{formatHoursDecimal(catData.hours)}</p>
                    <p className="text-[10px] font-bold text-steel-600 uppercase tracking-widest truncate max-w-[120px]" title={catData.name}>{catData.name}</p>
                  </div>
                </div>
              );
            })}

            {/* Separator */}
            {taskCards.length > 0 && <div className="w-px h-12 bg-steel-200 self-center mx-1" />}

            {/* Tasks */}
            {taskCards.map((taskData, idx) => {
              const colors = ['#6366f1', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#f59e0b'];
              const color = colors[idx % colors.length];
              return (
                <div key={taskData.name} className="bg-white rounded-xl px-4 py-3 border border-steel-100 shadow-sm flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                    <Clock className="h-5 w-5" style={{ color: color }} />
                  </div>
                  <div>
                    <p className="text-xl font-black leading-tight" style={{ color: color }}>{formatHoursDecimal(taskData.hours)}</p>
                    <p className="text-[10px] font-bold text-steel-600 uppercase tracking-widest truncate max-w-[120px]" title={taskData.name}>{taskData.name}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stats Cards - Line 2: Projects */}
          {projects.length > 0 && (
            <div className="flex flex-wrap gap-3 items-start">
              <span className="text-[10px] font-bold text-graphite-700 uppercase tracking-widest self-center">Projects:</span>
              {projects.map((project, idx) => {
                const isExpanded = expandedProjectCard === idx;
                const typeEntries = Object.entries(project.types).sort((a, b) => b[1] - a[1]);
                return (
                  <div
                    key={idx}
                    onClick={() => setExpandedProjectCard(isExpanded ? null : idx)}
                    className={`bg-white rounded-xl px-4 py-3 border shadow-sm cursor-pointer transition-all hover:shadow-md ${isExpanded ? 'border-brand-300' : 'border-steel-100'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center">
                        <Briefcase size={18} className="text-brand-500" />
                      </div>
                      <div>
                        <p className="text-xl font-black text-brand-500 leading-tight">{formatHoursDecimal(project.totalHours)}</p>
                        <p className="text-[10px] font-bold text-steel-600 uppercase tracking-widest">{project.name}</p>
                      </div>
                      <ChevronDown size={16} className={`text-steel-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-steel-100 space-y-1.5">
                        {typeEntries.map(([type, hrs]) => (
                          <div key={type} className="flex items-center justify-between gap-4">
                            <span className="text-xs text-steel-600 truncate">{type}</span>
                            <span className="text-xs font-bold text-graphite-900 whitespace-nowrap">{formatHoursDecimal(hrs)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-2xl border border-brand-200 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-steel-100 flex items-center justify-center">
                <Filter className="h-4 w-4 text-steel-600" />
              </div>
              <h3 className="text-sm font-black text-graphite-900 uppercase">Filters</h3>
              <span className="ml-auto px-2 py-0.5 text-[9px] font-bold bg-steel-100 text-steel-600 rounded-md">
                {totalCount} results
              </span>
            </div>

            <div className="flex flex-wrap gap-3 items-end justify-start">
              {/* Status */}
              <div>
                <label className="block text-[10px] font-bold text-graphite-700 uppercase tracking-widest mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className={`h-9 px-3 min-w-[120px] border rounded-xl text-sm font-medium focus:ring-1 focus:ring-brand-500 ${filters.status !== 'all' ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'}`}
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Category Filter */}
              <div className="relative filter-dropdown-container">
                <label className="block text-[10px] font-bold text-graphite-700 uppercase tracking-widest mb-1">
                  Category {filters.task_categories.length > 0 && <span className="text-brand-500">({filters.task_categories.length})</span>}
                </label>
                <button
                  onClick={() => { setShowCategoryDropdown(!showCategoryDropdown); setShowTaskDropdown(false); setShowTurbineDropdown(false); setShowUserDropdown(false); setShowProjectDropdown(false); }}
                  className={`h-9 px-3 min-w-[140px] flex items-center justify-between gap-2 border rounded-xl text-sm font-medium transition-colors ${filters.task_categories.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'}`}
                >
                  <span className="truncate">{filters.task_categories.length === 0 ? 'All' : `${filters.task_categories.length} selected`}</span>
                  <ChevronDown size={14} />
                </button>
                {showCategoryDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-steel-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                        <input type="text" placeholder="Search..." value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredCategories.map(category => (
                        <label key={category.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.task_categories.includes(category.id.toString()) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.task_categories.includes(category.id.toString())} onChange={() => handleCategoryToggle(category.id.toString())} className="w-4 h-4 text-brand-500 rounded" />
                          <span className="text-sm font-medium text-graphite-700 truncate">{category.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Task Filter */}
              <div className="relative filter-dropdown-container">
                <label className="block text-[10px] font-bold text-graphite-700 uppercase tracking-widest mb-1">
                  Task {filters.tasks.length > 0 && <span className="text-brand-500">({filters.tasks.length})</span>}
                </label>
                <button
                  onClick={() => { setShowTaskDropdown(!showTaskDropdown); setShowCategoryDropdown(false); setShowTurbineDropdown(false); setShowUserDropdown(false); setShowProjectDropdown(false); }}
                  className={`h-9 px-3 min-w-[140px] flex items-center justify-between gap-2 border rounded-xl text-sm font-medium transition-colors ${filters.tasks.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'}`}
                >
                  <span className="truncate">{filters.tasks.length === 0 ? 'All' : `${filters.tasks.length} selected`}</span>
                  <ChevronDown size={14} />
                </button>
                {showTaskDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-steel-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                        <input type="text" placeholder="Search..." value={taskSearch} onChange={(e) => setTaskSearch(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredTasks.map(task => (
                        <label key={task.value} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.tasks.includes(task.value) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.tasks.includes(task.value)} onChange={() => handleTaskToggle(task.value)} className="w-4 h-4 text-brand-500 rounded" />
                          <span className="text-sm font-medium text-graphite-700 truncate">{task.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* User Filter */}
              <div className="relative filter-dropdown-container">
                <label className="block text-[10px] font-bold text-graphite-700 uppercase tracking-widest mb-1">
                  User {filters.users.length > 0 && <span className="text-brand-500">({filters.users.length})</span>}
                </label>
                <button
                  onClick={() => { setShowUserDropdown(!showUserDropdown); setShowCategoryDropdown(false); setShowTaskDropdown(false); setShowTurbineDropdown(false); setShowProjectDropdown(false); }}
                  className={`h-9 px-3 min-w-[140px] flex items-center justify-between gap-2 border rounded-xl text-sm font-medium transition-colors ${filters.users.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'}`}
                >
                  <span className="truncate">{filters.users.length === 0 ? 'All' : `${filters.users.length} selected`}</span>
                  <ChevronDown size={14} />
                </button>
                {showUserDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-steel-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                        <input type="text" placeholder="Search..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredUsers.map(user => (
                        <label key={user.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.users.includes(user.id.toString()) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.users.includes(user.id.toString())} onChange={() => handleUserToggle(user.id.toString())} className="w-4 h-4 text-brand-500 rounded" />
                          <span className="text-sm font-medium text-graphite-700 truncate">{user.first_name} {user.last_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Project Filter */}
              <div className="relative filter-dropdown-container">
                <label className="block text-[10px] font-bold text-graphite-700 uppercase tracking-widest mb-1">
                  Project {filters.projects.length > 0 && <span className="text-brand-500">({filters.projects.length})</span>}
                </label>
                <button
                  onClick={() => { setShowProjectDropdown(!showProjectDropdown); setShowTurbineDropdown(false); setShowCategoryDropdown(false); setShowTaskDropdown(false); setShowUserDropdown(false); }}
                  className={`h-9 px-3 min-w-[140px] flex items-center justify-between gap-2 border rounded-xl text-sm font-medium transition-colors ${filters.projects.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'}`}
                >
                  <span className="truncate">{filters.projects.length === 0 ? 'All' : `${filters.projects.length} selected`}</span>
                  <ChevronDown size={14} />
                </button>
                {showProjectDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-steel-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                        <input type="text" placeholder="Search..." value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredProjects.map(project => (
                        <label key={project.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.projects.includes(project.id.toString()) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.projects.includes(project.id.toString())} onChange={() => handleProjectToggle(project.id.toString())} className="w-4 h-4 text-brand-500 rounded" />
                          <span className="text-sm font-medium text-graphite-700 truncate">{project.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Turbine Filter */}
              <div className="relative filter-dropdown-container">
                <label className="block text-[10px] font-bold text-graphite-700 uppercase tracking-widest mb-1">
                  Turbine {filters.turbines.length > 0 && <span className="text-brand-500">({filters.turbines.length})</span>}
                </label>
                <button
                  onClick={() => { setShowTurbineDropdown(!showTurbineDropdown); setShowProjectDropdown(false); setShowCategoryDropdown(false); setShowTaskDropdown(false); setShowUserDropdown(false); }}
                  className={`h-9 px-3 min-w-[140px] flex items-center justify-between gap-2 border rounded-xl text-sm font-medium transition-colors ${filters.turbines.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'}`}
                >
                  <span className="truncate">{filters.turbines.length === 0 ? 'All' : `${filters.turbines.length} selected`}</span>
                  <ChevronDown size={14} />
                </button>
                {showTurbineDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-steel-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                        <input type="text" placeholder="Search..." value={turbineSearch} onChange={(e) => setTurbineSearch(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredTurbines.map(turbine => (
                        <label key={turbine.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.turbines.includes(turbine.id.toString()) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.turbines.includes(turbine.id.toString())} onChange={() => handleTurbineToggle(turbine.id.toString())} className="w-4 h-4 text-brand-500 rounded" />
                          <span className="text-sm font-medium text-graphite-700 truncate">T{turbine.turbine_number}{turbine.turbine_id ? ` (${turbine.turbine_id})` : ''}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Date From */}
              <div>
                <label className="block text-[10px] font-bold text-graphite-700 uppercase tracking-widest mb-1">From</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  className={`h-9 px-3 border rounded-xl text-sm font-medium focus:ring-1 focus:ring-brand-500 ${filters.date_from ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'}`}
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-[10px] font-bold text-graphite-700 uppercase tracking-widest mb-1">To</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  className={`h-9 px-3 border rounded-xl text-sm font-medium focus:ring-1 focus:ring-brand-500 ${filters.date_to ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'}`}
                />
              </div>

              {/* Clear Filters */}
              {hasFilters && (
                <button
                  onClick={handleResetFilters}
                  className="h-9 px-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs font-bold hover:bg-red-100 transition-colors flex items-center gap-1"
                >
                  <X size={14} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          {timesheets.length === 0 ? (
            <div className="bg-white rounded-2xl border border-steel-100 shadow-sm p-10 text-center">
              <div className="w-12 h-12 bg-steel-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Clock className="h-6 w-6 text-steel-400" />
              </div>
              <p className="text-sm font-bold text-graphite-900 mb-1">No timesheets found</p>
              <p className="text-xs text-steel-400">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-brand-200 shadow-sm overflow-hidden">
              {/* Toolbar */}
              <div className="px-4 py-2 border-b border-steel-100 flex items-center justify-between bg-steel-50">
                <button
                  onClick={() => {
                    const grouped = groupTimesheets(timesheets);
                    const allGroupKeys = grouped.filter(item => item.isGroup).map(item => item.key);
                    const allExpanded = allGroupKeys.every(key => expandedGroups[key]);
                    if (allExpanded) setExpandedGroups({});
                    else {
                      const newExpanded = {};
                      allGroupKeys.forEach(key => { newExpanded[key] = true; });
                      setExpandedGroups(newExpanded);
                    }
                  }}
                  className="text-xs font-bold text-graphite-700 hover:text-brand-500 flex items-center gap-1"
                >
                  {(() => {
                    const grouped = groupTimesheets(timesheets);
                    const allGroupKeys = grouped.filter(item => item.isGroup).map(item => item.key);
                    const allExpanded = allGroupKeys.length > 0 && allGroupKeys.every(key => expandedGroups[key]);
                    return allExpanded ? <><ChevronDown size={14} /> Collapse All</> : <><ChevronRight size={14} /> Expand All</>;
                  })()}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-steel-50 border-b border-steel-100">
                      <th className="px-3 py-3 text-left w-10">
                        <input type="checkbox" checked={selectedIds.length === timesheets.length && timesheets.length > 0} onChange={handleSelectAll} className="w-4 h-4 text-brand-500 rounded" />
                      </th>
                      <th className="px-3 py-3 text-left w-10"></th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">User</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Project</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Turbine</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Check In/Out</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider cursor-pointer hover:text-graphite-900" onClick={() => setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}>
                        Date {sortDirection === 'desc' ? '▼' : '▲'}
                      </th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Time</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Hours</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Category/Task</th>
                      <th className="px-3 py-3 text-center text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Notes</th>
                      <th className="px-3 py-3 text-center text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-steel-100">
                    {groupTimesheets(timesheets).map((item) => {
                      if (item.isGroup) {
                        const isExpanded = expandedGroups[item.key];
                        const hasDiscrepancy = item.items.some(ts => ts.time_discrepancy?.has_discrepancy);
                        return (
                          <React.Fragment key={item.key}>
                            <tr className={`hover:bg-steel-50 cursor-pointer ${hasDiscrepancy ? 'bg-amber-50/50' : ''}`} onClick={() => toggleGroup(item.key)}>
                              <td className="px-3 py-2">
                                <span className="text-steel-400">{isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                              </td>
                              <td className="px-3 py-2">
                                {item.user.profile_picture ? (
                                  <img src={`${process.env.REACT_APP_API_URL}${item.user.profile_picture}`} alt="" className="w-8 h-8 rounded-lg object-cover border border-steel-200" />
                                ) : (
                                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-[10px] font-black text-white">
                                    {item.user.full_name?.charAt(0) || 'U'}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm font-bold text-graphite-900">{item.user.full_name}</td>
                              <td className="px-3 py-2 text-sm text-steel-600">
                                {(() => {
                                  const uniqueProjects = [...new Set(item.items.map(ts => ts.projekt?.name).filter(Boolean))];
                                  if (uniqueProjects.length === 1) return uniqueProjects[0];
                                  if (uniqueProjects.length > 1) return `Multiple (${uniqueProjects.length})`;
                                  return '-';
                                })()}
                              </td>
                              <td className="px-3 py-2 text-sm text-steel-600">
                                {(() => {
                                  const uniqueTurbines = [...new Set(item.items.map(ts => ts.project_turbine_name).filter(Boolean))];
                                  if (uniqueTurbines.length === 1) return uniqueTurbines[0];
                                  if (uniqueTurbines.length > 1) return `Multiple (${uniqueTurbines.length})`;
                                  return '-';
                                })()}
                              </td>
                              <td className="px-3 py-2">
                                {(() => {
                                  const checkIns = item.items.map(ts => ts.qr_check_in).filter(Boolean);
                                  const checkOuts = item.items.map(ts => ts.qr_check_out).filter(Boolean);
                                  return (
                                    <div className="space-y-0.5">
                                      <div className="flex items-center gap-1 text-xs text-emerald-600">
                                        <LogIn size={12} />
                                        <span>{checkIns.length > 0 ? checkIns[0] : '-'}</span>
                                        {hasDiscrepancy && <AlertTriangle size={12} className="text-amber-500" />}
                                      </div>
                                      <div className="flex items-center gap-1 text-xs text-red-500">
                                        <LogOut size={12} />
                                        <span>{checkOuts.length > 0 ? checkOuts[checkOuts.length - 1] : '-'}</span>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-3 py-2 text-sm text-steel-600">{formatDate(item.date)}</td>
                              <td className="px-3 py-2">
                                <span className="px-2 py-0.5 text-[9px] font-bold bg-steel-100 text-steel-600 rounded-md">{item.items.length} entries</span>
                              </td>
                              <td className="px-3 py-2 text-sm font-bold text-graphite-900">{item.displayHours}</td>
                              <td className="px-3 py-2">
                                {(() => {
                                  const uniqueCategories = [...new Set(item.items.map(ts => ts.task_category_name || ts.type).filter(Boolean))];
                                  if (uniqueCategories.length === 1) {
                                    return <span className="px-2 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-600 rounded-md">{uniqueCategories[0]}</span>;
                                  }
                                  if (uniqueCategories.length > 1) {
                                    return <span className="px-2 py-0.5 text-[9px] font-bold bg-steel-100 text-steel-600 rounded-md">Multiple</span>;
                                  }
                                  return <span className="px-2 py-0.5 text-[9px] font-bold bg-steel-100 text-steel-600 rounded-md">-</span>;
                                })()}
                              </td>
                              <td className="px-3 py-2 text-center">{getStatusBadge(item.groupStatus)}</td>
                              <td className="px-3 py-2 text-xs text-steel-400">{item.items.length} entries</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center gap-1">
                                  <button onClick={(e) => { e.stopPropagation(); item.items.forEach(ts => handleSingleApprove(ts.id)); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 hover:bg-emerald-100 transition-colors" title="Approve All">
                                    <CheckCircle size={14} />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setSelectedIds(item.items.map(ts => ts.id)); setShowRejectModal(true); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors" title="Reject All">
                                    <XCircle size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded && item.items.map((ts) => (
                              <tr key={ts.id} className="bg-steel-50/50 hover:bg-steel-100">
                                <td className="px-3 py-2 pl-6">
                                  <input type="checkbox" checked={selectedIds.includes(ts.id)} onChange={() => handleSelectOne(ts.id)} className="w-4 h-4 text-brand-500 rounded" />
                                </td>
                                <td className="px-3 py-2"></td>
                                <td className="px-3 py-2 text-sm text-steel-600 pl-6">↳ {ts.user.full_name}</td>
                                <td className="px-3 py-2 text-sm text-graphite-700">{ts.projekt?.name || 'N/A'}</td>
                                <td className="px-3 py-2 text-sm text-graphite-700">{ts.project_turbine_name || '-'}</td>
                                <td className="px-3 py-2">
                                  <div className="space-y-0.5">
                                    <div className="flex items-center gap-1 text-xs text-emerald-600">
                                      <LogIn size={12} />
                                      <span>{ts.qr_check_in || '-'}</span>
                                      {ts.time_discrepancy?.has_discrepancy && (
                                        <AlertTriangle size={12} className={getSeverityColor(ts.time_discrepancy.severity)} onClick={(e) => { e.stopPropagation(); handleDiscrepancyClick(ts); }} style={{ cursor: 'pointer' }} />
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-red-500">
                                      <LogOut size={12} />
                                      <span>{ts.qr_check_out || '-'}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-sm text-graphite-700">{formatDate(ts.date)}</td>
                                <td className="px-3 py-2 text-sm text-graphite-700">{ts.start_time} - {ts.end_time}</td>
                                <td className="px-3 py-2 text-sm font-bold text-graphite-900">{ts.worked_hours}</td>
                                <td className="px-3 py-2">
                                  <span className="px-2 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-600 rounded-md">{getFullType(ts)}</span>
                                </td>
                                <td className="px-3 py-2 text-center">{getStatusBadge(ts.status)}</td>
                                <td className="px-3 py-2 text-xs text-graphite-700 max-w-[150px] truncate" title={ts.note}>{ts.note || '-'}</td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center justify-center gap-1">
                                    {ts.status === 'pending' && (
                                      <>
                                        <button onClick={(e) => { e.stopPropagation(); handleSingleApprove(ts.id); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 hover:bg-emerald-100 transition-colors" title="Approve">
                                          <CheckCircle size={14} />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); handleSingleRejectClick(ts.id); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors" title="Reject">
                                          <XCircle size={14} />
                                        </button>
                                      </>
                                    )}
                                    <button onClick={(e) => { e.stopPropagation(); handleEditClick(ts); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-50 text-brand-500 hover:bg-brand-100 transition-colors" title="Edit">
                                      <Edit2 size={14} />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleHistoryClick(ts); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-50 text-violet-500 hover:bg-violet-100 transition-colors" title="History">
                                      <History size={14} />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleSingleDelete(ts.id); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-steel-50 text-steel-400 hover:bg-red-50 hover:text-red-500 transition-colors" title="Delete">
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      } else {
                        const ts = item;
                        return (
                          <tr key={ts.id} className={`hover:bg-steel-50 ${ts.time_discrepancy?.has_discrepancy ? 'bg-amber-50/50' : ''}`}>
                            <td className="px-3 py-2">
                              <input type="checkbox" checked={selectedIds.includes(ts.id)} onChange={() => handleSelectOne(ts.id)} className="w-4 h-4 text-brand-500 rounded" />
                            </td>
                            <td className="px-3 py-2">
                              {ts.user.profile_picture ? (
                                <img src={`${process.env.REACT_APP_API_URL}${ts.user.profile_picture}`} alt="" className="w-8 h-8 rounded-lg object-cover border border-steel-200" />
                              ) : (
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-[10px] font-black text-white">
                                  {ts.user.full_name?.charAt(0) || 'U'}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm font-bold text-graphite-900">{ts.user.full_name}</td>
                            <td className="px-3 py-2 text-sm text-graphite-700">{ts.projekt?.name || 'N/A'}</td>
                            <td className="px-3 py-2 text-sm text-graphite-700">{ts.project_turbine_name || '-'}</td>
                            <td className="px-3 py-2">
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-1 text-xs text-emerald-600">
                                  <LogIn size={12} />
                                  <span>{ts.qr_check_in || '-'}</span>
                                  {ts.time_discrepancy?.has_discrepancy && (
                                    <AlertTriangle size={12} className={getSeverityColor(ts.time_discrepancy.severity)} onClick={(e) => { e.stopPropagation(); handleDiscrepancyClick(ts); }} style={{ cursor: 'pointer' }} />
                                  )}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-red-500">
                                  <LogOut size={12} />
                                  <span>{ts.qr_check_out || '-'}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-sm text-graphite-700">{formatDate(ts.date)}</td>
                            <td className="px-3 py-2 text-sm text-graphite-700">{ts.start_time} - {ts.end_time}</td>
                            <td className="px-3 py-2 text-sm font-bold text-graphite-900">{ts.worked_hours}</td>
                            <td className="px-3 py-2">
                              <span className="px-2 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-600 rounded-md">{getFullType(ts)}</span>
                            </td>
                            <td className="px-3 py-2 text-center">{getStatusBadge(ts.status)}</td>
                            <td className="px-3 py-2 text-xs text-graphite-700 max-w-[150px] truncate" title={ts.note}>{ts.note || '-'}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-center gap-1">
                                {ts.status === 'pending' && (
                                  <>
                                    <button onClick={() => handleSingleApprove(ts.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 hover:bg-emerald-100 transition-colors" title="Approve">
                                      <CheckCircle size={14} />
                                    </button>
                                    <button onClick={() => handleSingleRejectClick(ts.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors" title="Reject">
                                      <XCircle size={14} />
                                    </button>
                                  </>
                                )}
                                <button onClick={() => handleEditClick(ts)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-50 text-brand-500 hover:bg-brand-100 transition-colors" title="Edit">
                                  <Edit2 size={14} />
                                </button>
                                <button onClick={() => handleHistoryClick(ts)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-50 text-violet-500 hover:bg-violet-100 transition-colors" title="History">
                                  <History size={14} />
                                </button>
                                <button onClick={() => handleSingleDelete(ts.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-steel-50 text-steel-400 hover:bg-red-50 hover:text-red-500 transition-colors" title="Delete">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-3 border-t border-steel-100 flex items-center justify-between">
                  <span className="text-[10px] text-steel-500 font-medium">
                    Showing <span className="font-bold text-graphite-700">{((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)}</span> of <span className="font-bold text-graphite-700">{totalCount}</span>
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronsLeft size={16} />
                    </button>
                    <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronLeft size={16} />
                    </button>
                    <div className="flex items-center gap-1 mx-1">
                      {(() => {
                        const pages = [];
                        const showPages = 5;
                        let start = Math.max(1, currentPage - Math.floor(showPages / 2));
                        let end = Math.min(totalPages, start + showPages - 1);
                        if (end - start + 1 < showPages) start = Math.max(1, end - showPages + 1);
                        for (let i = start; i <= end; i++) {
                          pages.push(
                            <button key={i} onClick={() => setCurrentPage(i)} className={`h-8 w-8 flex items-center justify-center text-xs font-bold rounded-lg transition-colors ${currentPage === i ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-steel-600 bg-steel-100 hover:bg-steel-200'}`}>{i}</button>
                          );
                        }
                        return pages;
                      })()}
                    </div>
                    <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronRightIcon size={16} />
                    </button>
                    <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && user?.isCompanyAdmin && (
        <TimesheetSettings />
      )}

      {/* Action Bar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-graphite-900 text-white rounded-2xl shadow-2xl px-6 py-3 flex items-center gap-4">
          <span className="text-sm font-bold">{selectedIds.length} selected</span>
          <div className="h-6 w-px bg-graphite-700" />
          <button onClick={handleApprove} className="h-8 px-3 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition-colors flex items-center gap-1">
            <CheckCircle size={14} /> Approve
          </button>
          <button onClick={handleReject} className="h-8 px-3 bg-red-500 text-white rounded-lg text-xs font-bold hover:bg-red-600 transition-colors flex items-center gap-1">
            <XCircle size={14} /> Reject
          </button>
          <button onClick={() => { const ts = timesheets.find(t => t.id === selectedIds[0]); if (ts) handleEditClick(ts); }} disabled={selectedIds.length !== 1} className="h-8 px-3 bg-graphite-700 text-white rounded-lg text-xs font-bold hover:bg-steel-600 disabled:opacity-40 transition-colors flex items-center gap-1">
            <Edit2 size={14} /> Edit
          </button>
          <button onClick={handleDelete} className="h-8 px-3 bg-graphite-700 text-white rounded-lg text-xs font-bold hover:bg-steel-600 transition-colors flex items-center gap-1">
            <Trash2 size={14} /> Delete
          </button>
          <button onClick={() => setSelectedIds([])} className="h-8 px-3 bg-graphite-700 text-white rounded-lg text-xs font-bold hover:bg-steel-600 transition-colors">
            Clear
          </button>
        </div>
      )}

      {/* Modals */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowRejectModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="h-4 w-4 text-red-500" />
              </div>
              <h2 className="text-sm font-black text-graphite-900 uppercase">Reject Timesheets</h2>
            </div>
            <div className="p-4">
              <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Rejection Reason</label>
              <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Enter reason..." rows={4} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 placeholder-steel-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none" />
            </div>
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2">
              <button onClick={() => setShowRejectModal(false)} className="h-9 px-4 text-steel-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors">Cancel</button>
              <button onClick={confirmReject} className="h-9 px-4 bg-red-500 text-white rounded-xl hover:bg-red-600 font-bold text-xs uppercase tracking-wide transition-all">Reject</button>
            </div>
          </div>
        </div>
      )}

      {showSingleRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowSingleRejectModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="h-4 w-4 text-red-500" />
              </div>
              <h2 className="text-sm font-black text-graphite-900 uppercase">Reject Timesheet</h2>
            </div>
            <div className="p-4">
              <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Rejection Reason</label>
              <textarea value={singleRejectionReason} onChange={(e) => setSingleRejectionReason(e.target.value)} placeholder="Enter reason..." rows={4} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 placeholder-steel-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none" />
            </div>
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2">
              <button onClick={() => setShowSingleRejectModal(false)} className="h-9 px-4 text-steel-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors">Cancel</button>
              <button onClick={confirmSingleReject} className="h-9 px-4 bg-red-500 text-white rounded-xl hover:bg-red-600 font-bold text-xs uppercase tracking-wide transition-all">Reject</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <Trash2 className="h-4 w-4 text-red-500" />
              </div>
              <h2 className="text-sm font-black text-graphite-900 uppercase">Confirm Delete</h2>
            </div>
            <div className="p-4">
              <p className="text-sm text-graphite-700">
                {deleteType === 'single' ? 'Delete this timesheet? This cannot be undone.' : `Delete ${selectedIds.length} timesheet(s)? This cannot be undone.`}
              </p>
            </div>
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2">
              <button onClick={() => setShowDeleteModal(false)} className="h-9 px-4 text-steel-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors">Cancel</button>
              <button onClick={confirmDelete} className="h-9 px-4 bg-red-500 text-white rounded-xl hover:bg-red-600 font-bold text-xs uppercase tracking-wide transition-all">Delete</button>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && historyTimesheet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowHistoryModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <History className="h-4 w-4 text-violet-600" />
                </div>
                <h2 className="text-sm font-black text-graphite-900 uppercase">Timesheet History</h2>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="p-1.5 rounded-lg hover:bg-steel-100 text-steel-400"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="p-3 bg-steel-50 rounded-xl space-y-1">
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">User:</strong> {historyTimesheet.user.full_name}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Project:</strong> {historyTimesheet.projekt?.name || 'N/A'}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Date:</strong> {formatDate(historyTimesheet.date)}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Time:</strong> {historyTimesheet.start_time} - {historyTimesheet.end_time}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Hours:</strong> {historyTimesheet.worked_hours}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Type:</strong> {getFullType(historyTimesheet)}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Status:</strong> {historyTimesheet.status}</p>
              </div>
              {historyTimesheet.approved_by && (
                <div className="p-3 bg-emerald-50 rounded-xl space-y-1">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase">Approval Info</p>
                  <p className="text-xs text-steel-600">Approved by: {historyTimesheet.approved_by}</p>
                  <p className="text-xs text-steel-600">Date: {formatDate(historyTimesheet.approved_date)}</p>
                </div>
              )}
              {historyTimesheet.rejected_by && (
                <div className="p-3 bg-red-50 rounded-xl space-y-1">
                  <p className="text-[10px] font-bold text-red-600 uppercase">Rejection Info</p>
                  <p className="text-xs text-steel-600">Rejected by: {historyTimesheet.rejected_by}</p>
                  <p className="text-xs text-steel-600">Date: {formatDate(historyTimesheet.rejected_date)}</p>
                  {historyTimesheet.rejection_reason && <p className="text-xs text-steel-600">Reason: {historyTimesheet.rejection_reason}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingTimesheet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowEditModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-2xl shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
                  <Edit2 className="h-4 w-4 text-brand-500" />
                </div>
                <div>
                  <h2 className="text-sm font-black text-graphite-900 uppercase">Edit Timesheet</h2>
                  <p className="text-[10px] text-steel-400">{editingTimesheet.user.full_name}</p>
                </div>
              </div>
              <button onClick={() => setShowEditModal(false)} className="p-1.5 rounded-lg hover:bg-steel-100 text-steel-400"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Date</label>
                  <input type="date" value={editFormData.date} onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Project</label>
                  <select value={editFormData.projekt} onChange={(e) => setEditFormData({ ...editFormData, projekt: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500">
                    <option value="">Select Project</option>
                    {availableProjects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Start Time</label>
                  <input type="time" value={editFormData.start_time} onChange={(e) => setEditFormData({ ...editFormData, start_time: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">End Time</label>
                  <input type="time" value={editFormData.end_time} onChange={(e) => setEditFormData({ ...editFormData, end_time: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Break (min)</label>
                  <input type="number" min="0" max="480" value={editFormData.break_duration} onChange={(e) => setEditFormData({ ...editFormData, break_duration: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Category</label>
                  <select value={editFormData.type} onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500">
                    <option value="">Select Category</option>
                    {taskCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Task</label>
                  <select value={editFormData.admin_type} onChange={(e) => setEditFormData({ ...editFormData, admin_type: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500">
                    <option value="">None</option>
                    {taskSubcategories.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Note</label>
                  <input type="text" value={editFormData.note} onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })} placeholder="Optional note..." className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 placeholder-steel-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2">
              <button onClick={() => setShowEditModal(false)} className="h-9 px-4 text-steel-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors">Cancel</button>
              <button onClick={handleSaveEdit} className="h-9 px-4 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold text-xs uppercase tracking-wide transition-all shadow-lg shadow-brand-500/20">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {showDiscrepancyModal && discrepancyData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDiscrepancyModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                </div>
                <h2 className="text-sm font-black text-graphite-900 uppercase">Time Discrepancy</h2>
              </div>
              <button onClick={() => setShowDiscrepancyModal(false)} className="p-1.5 rounded-lg hover:bg-steel-100 text-steel-400"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="p-3 bg-steel-50 rounded-xl space-y-1">
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">User:</strong> {discrepancyData.user?.full_name}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Project:</strong> {discrepancyData.project}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Date:</strong> {formatDate(discrepancyData.date)}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Severity:</strong> <span className={`font-bold uppercase ${getSeverityColor(discrepancyData.severity)}`}>{discrepancyData.severity}</span></p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-emerald-50 rounded-xl">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase mb-2">QR System (Actual)</p>
                  <p className="text-xs text-steel-600">Check-In: {discrepancyData.qr_start}</p>
                  <p className="text-xs text-steel-600">Check-Out: {discrepancyData.qr_end}</p>
                </div>
                <div className="p-3 bg-brand-50 rounded-xl">
                  <p className="text-[10px] font-bold text-brand-600 uppercase mb-2">Timesheet (Submitted)</p>
                  <p className="text-xs text-steel-600">Start: {discrepancyData.ts_start}</p>
                  <p className="text-xs text-steel-600">End: {discrepancyData.ts_end}</p>
                </div>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl space-y-1">
                <p className="text-[10px] font-bold text-amber-600 uppercase">Analysis</p>
                <p className="text-xs text-steel-600">Start Diff: {formatTimeDifference(discrepancyData.start_diff_minutes)}</p>
                <p className="text-xs text-steel-600">End Diff: {formatTimeDifference(discrepancyData.end_diff_minutes)}</p>
                <p className="text-xs text-steel-600">Total Diff: {formatTimeDifference(discrepancyData.total_diff_minutes)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-2.5 bg-graphite-900 text-white text-sm font-bold rounded-xl shadow-lg">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default TaskBasedTimesheetManagement;
